/**
 * Flow C consult status — GET /api/consults/{id}/status (contract §2). Added to fix
 * BUG #7: `ConsultRoomFrame` used to poll `GET /api/appointments/{id}/session`, which is
 * OWNER-ONLY (`require_tenant_owner`) — a 401 every 4s for the whole time a PATIENT sat
 * on the page, and the one-shot quote fetch it relied on instead for `authState` meant
 * the join door never unlocked without a manual reload once the authorisation actually
 * landed.
 *
 * Both roles read this endpoint: the patient supplies `token`; the practitioner omits it
 * and is identified by the owner session instead (same "omit token" convention as
 * `getConsultAccess`, contract §2). Never call `getConsultSession` from a patient-facing
 * component — that is the regression this hook exists to make impossible.
 *
 * `authState` is the MONEY question only ("may the door open?") and is four-valued —
 * `none | awaiting | authorised | failed` — reusing the same mapping `/quote` uses; a
 * captured consult still reads `authorised`, a cancelled one reads `none`. Whether the
 * consultation is OVER, and how it ended, is `sessionStatus` / `outcome` instead. Never
 * gate joinability on `authState === "authorised"` alone — that is exactly what
 * `canJoin` exists to answer instead; a component reading this hook must gate on
 * `canJoin`, and use `authState` only to explain WHY not, when it is false.
 *
 * Polling stops once the outcome is final: a terminal `sessionStatus` (the session state
 * machine's terminal subset — captured | void_no_show | cancelled; `expired` was an
 * eighth SESSION_STATES member nothing ever set — a lapsed hold is `cancelled`, not
 * `expired` — and the backend dropped it (schemas.py SESSION_STATES, now seven values;
 * the PAYMENT state `expired` is a different vocabulary and stays). Contract §7
 * lists the full session state set but does not itself designate which are terminal) or
 * the one terminal `authState`, `failed` — there is nothing left to learn by continuing
 * to ask.
 *
 * `gatewayApi` is an optional trailing param defaulting to the module singleton,
 * mirroring the component-level DI rule (contract §9).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import defaultGatewayApi from "../api";
import { extractGatewayErrorCode } from "../errors";
import { SESSION_MONEY_FINAL_STATES } from "../contract";

const TERMINAL_AUTH_STATES = ["failed"];
const POLL_INTERVAL_MS = 4000;

const isTerminal = (status) =>
  !!status
  && (SESSION_MONEY_FINAL_STATES.includes(status.sessionStatus)
    || TERMINAL_AUTH_STATES.includes(status.authState));

export function useConsultStatus(
  appointmentId,
  token,
  { poll = true, intervalMs = POLL_INTERVAL_MS } = {},
  gatewayApi = defaultGatewayApi,
) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const reload = useCallback(async () => {
    if (!appointmentId) {
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const status = await gatewayApi.getConsultStatus(appointmentId, token);
      setData(status);
      setError(null);
      return status;
    } catch (e) {
      setError({ code: extractGatewayErrorCode(e), raw: e });
      return null;
    } finally {
      setLoading(false);
    }
  }, [appointmentId, token, gatewayApi]);

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      const status = await reload();
      if (cancelled || !poll) return;
      if (isTerminal(status)) return;
      timerRef.current = setTimeout(tick, intervalMs);
    };

    tick();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // `reload` is stable per (appointmentId, token, gatewayApi) via its own useCallback
    // deps; re-running this effect on every render of `reload` would restart the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId, token, poll, intervalMs]);

  return { data, loading, error, reload };
}

export default useConsultStatus;
